import {ellipsis} from 'polished';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf, Renderer} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {AccountModel} from '../../../models/api/accountModel';
import {
  createFormFieldHoverStyle,
  createFormInputFieldHeight,
} from '../../accounts/createAccount/createAccountStyles';
import {AccountAvatar} from '../avatars/accountAvatar';
import {SearchAccountsDropdown} from '../dropdowns/search/searchAccountsDropdown';
import {TooltipExplicitCondition} from '../tooltips/tooltipExplicitCondition';
import {CreateFormInputDisplay} from './createFormInputDisplay';
import {addCreateFormInputDisabledStyles} from './createFormInputHelpers';

/*
 * Constants.
 */

const tooltipMaxWidth = 240; // px.

/*
 * Props.
 */

interface AccountPopoverProps {
  title: ReactNode;
  account?: AccountModel;
  isModalInitiallyOpen?: boolean;
  isDisabled?: boolean;
  renderDisabledTooltip?: Renderer;
  onSelect: HandlerOf<number>;
  onDelete?: Handler;
}

/*
 * Style.
 */

interface StyledButtonDivProps {
  $isDisabled?: boolean;
}

const StyledButtonDiv = styled.div<StyledButtonDivProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 5px 8px;
  width: fit-content;
  height: ${createFormInputFieldHeight}px;

  ${createFormFieldHoverStyle}
  ${(p) => addCreateFormInputDisabledStyles(p.$isDisabled)}
`;

const StyledPlaceholderTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
  white-space: nowrap;
`;

const StyledAccountNameSpan = styled.span`
  color: ${(p) => p.theme.greys.shade90};
  white-space: nowrap;
  ${ellipsis()}
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/*
 * Component.
 */

export const AccountPopover: FC<AccountPopoverProps> = (props) => {
  const theme = useUserTheme();
  const {title, account, isModalInitiallyOpen, isDisabled, renderDisabledTooltip, onSelect, onDelete} = props;

  return (
    <CreateFormInputDisplay
      title={title}
      input={
        <PopoverCoordinator
          isDisabled={isDisabled}
          renderButton={() => (
            <TooltipCoordinator
              render={renderDisabledTooltip}
              maxWidth={tooltipMaxWidth}
              placement="bottom-start"
            >
              <TooltipExplicitCondition isEnabled={isDisabled} />
              <StyledButtonDiv $isDisabled={isDisabled}>
                {renderAccountOrPlaceholder(account)}
                <Icon name="chevronDownSmall" color={theme.greys.shade60} />
              </StyledButtonDiv>
            </TooltipCoordinator>
          )}
          renderModal={(popoverProps) => (
            <SearchAccountsDropdown
              popoverProps={popoverProps}
              selectedAccountId={account?.id}
              onConfirm={onSelect}
              onDelete={onDelete}
            />
          )}
          isModalInitiallyOpen={Boolean(isModalInitiallyOpen)}
        />
      }
    />
  );
};

/*
 * Helpers.
 */

function renderAccountOrPlaceholder(account?: AccountModel) {
  if (!account) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledButtonContentDiv>
            <Icon name="briefcaseOutlineSmall" color={theme.greys.shade60} />
            <StyledPlaceholderTextSpan>
              <Trans id="create-form-account-placeholder">Add to account</Trans>
            </StyledPlaceholderTextSpan>
          </StyledButtonContentDiv>
        )}
      </UserThemeConsumer>
    );
  }
  return (
    <StyledButtonContentDiv>
      <AccountAvatar account={account} size={20} avatarIconSize={12} />
      <StyledAccountNameSpan>{account.name}</StyledAccountNameSpan>
    </StyledButtonContentDiv>
  );
}
